import React from 'react'
import styles from './InvoiceDetails.module.scss'
import Layout from '../../components/core/Layout';
import { useParams } from 'react-router-dom';
import { getTaxRate, getOfficeWorkFeeOptions, getOfficeWorkFee, postOfficeWorkFee } from '../../apis/invoice-api';
import { INVOICE_TAB_INDEX, InvStatus } from '../../libs/constants';
import { str2number } from '../../libs/helpers';
import { InvoiceMasterData } from "../../apis/models/InvoiceMasterRequest";
import { error, success } from '../../apis/reducers/snackbarsReducer';
import { useDispatch, useSelector } from 'react-redux';
import { getInvoiceMaster, putInvoiceMaster } from '../../apis/invmaster-api';
import { TaxResponseData } from '../../apis/models/TaxResponse';
import DateWithoutTime from '../../libs/dateWithoutTime';
import InvoiceHeader from '../../components/InvoiceDetailsPage/header/InvoiceHeader';
import { RootState } from '../../apis/store';
import { saveMasterInfo } from '../../apis/reducers/invoiceMasterReducer';
import OfficeWorkFee, { OfficeWorkFeeTabType } from '../../components/InvoiceDetailsPage/OfficeWorkFee';
import { OfficeWorkOption, OfficeWorkResponseData } from '../../apis/models/OfficeWorkResponse';
import { OfficeWorkRequest } from '../../apis/models/OfficeWorkRequest';

const officeFeeCompare = (a: OfficeWorkFeeTabType, b: OfficeWorkFeeTabType) => {
  return DateWithoutTime.sortAsc(a.dateAdded, b.dateAdded)
}

const InvoiceOfficeWorkFeePage = () => {
  const invoiceMasterState = useSelector((state: RootState) => state.invoiceMaster);
  const dispatch = useDispatch();
  const { id } = useParams();
  const [invoiceMasterData, setInvoiceMasterData] = React.useState<InvoiceMasterData | null>(invoiceMasterState.masterData);
  const [taxItem, setTaxItem] = React.useState<TaxResponseData | null>(null);
  const [officeWorkOptions, setOfficeWorkOptions] = React.useState<OfficeWorkOption[]>([]);
  const [officeWorkFees, setOfficeWorkFees] = React.useState<OfficeWorkFeeTabType[]>([]);
  const [notYetSaved, setNotYetSaved] = React.useState<boolean>(false);
  const [isChanged, setIsChanged] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (id) {
      loadAllData();
    }
  },[id])

  const hasError = React.useMemo(() => {
    return officeWorkFees.some(fee => {
      if (Object.keys(fee.error).length !== 0) {
        return fee.error.dateAdded || fee.error.total || fee.error.officeWorkFees.length > 0
      } else return false;
    })
  },[officeWorkFees]);

  const loadAllData = () => {
    getInvoiceMasterData();
    getOfficeWork();
    setNotYetSaved(false);
  }

  const onSaveAndCommit = async () => {
    const isSuccess = await saveOfficeWorkFee();
    if (isSuccess){
      onChangeInvoiceStatus(InvStatus.CONFIRMED)
    }
  }

  const onChangeInvoiceStatus = async (status: InvStatus) => {
    try {
      await putInvoiceMaster(id!, {status: status})
      dispatch(success("請求ステータスを変更しました"));
      loadAllData();
    }catch (err) {
      dispatch(error("請求ステータスの変更に失敗しました"));
    }
  }

  const getInvoiceMasterData = () => {
    getInvoiceMaster(id)
    .then(function (response) {
        setInvoiceMasterData(response.data.data);
        dispatch(saveMasterInfo({masterData: response.data.data}))
        getTaxRate(new Date(response.data.data.from).toString())
          .then(res => setTaxItem(res.data.data))
          .catch(err => console.log(err))
    })
    .catch(function (error) {
        console.log(error);
    })
  }

  const getOfficeWork = () => {
    if (!id) {
      return
    }
    const promises = [getOfficeWorkFeeOptions(id), getOfficeWorkFee(id)]
    Promise.all(promises)
      .then((values) => {
        setOfficeWorkOptions(values[0].data.data);
        setOfficeWorkFees(convertResponseToTabType(values[1].data.data));
      })
      .catch((err) => {})
  }

  const convertResponseToTabType = (datas: OfficeWorkResponseData[]): OfficeWorkFeeTabType[] => {
    const result: OfficeWorkFeeTabType[] = [];
    datas.forEach((fee: OfficeWorkResponseData) => {
      const addData = {
        officeWorkFeeOptionId: fee.officeWorkFeeOptionId,
        officeWorkFeeOption: fee.officeWorkFeeOption,
        quantity: fee.quantity + "",
      }
      const exists = result.find(r => r.dateAdded.equals(new DateWithoutTime(fee.dateAdded)))
      if (exists) {
        exists.officeWorkFees.push(addData)
      } else {
        result.push(
          {
            dateAdded: new DateWithoutTime(fee.dateAdded),
            remarks: fee.memo,
            officeWorkFees: [addData],
            error: {officeWorkFees: []}
          }
        )
      }
    })
    result.sort(officeFeeCompare);
    return result;
  }

  const onChangeFees = (items: OfficeWorkFeeTabType[]) => {
    setOfficeWorkFees(items);
    setNotYetSaved(true);
  }

  const saveOfficeWorkFee = async () => {
    if (!id) {
      return false;
    }
    const req: OfficeWorkRequest[] = [];
    officeWorkFees.forEach(rowData => {
      rowData.officeWorkFees.forEach(fee => {
        const reqItem: OfficeWorkRequest = {
          invoiceMasterId: id,
          dateAdded: rowData.dateAdded.getValue4Server(),
          memo: rowData.remarks,
          officeWorkFeeOptionId: fee.officeWorkFeeOptionId!,
          quantity: str2number(fee.quantity)
        }
        req.push(reqItem);
      })
    });

    try {
      await postOfficeWorkFee(id, req);
      dispatch(success("保存しました"));
      loadAllData();

      return true;
    } catch (e) {
      dispatch(error("保存に失敗しました"))
    }
    return false;
  }

  return (
    <Layout withBreadcrumbs={true} bgColor="#FAFAFA">
      <React.Fragment>
        <div className={styles.Gray}>
          <div>
            <InvoiceHeader
              invoiceMaster={invoiceMasterData ?? undefined}
              tax={taxItem ?? undefined}
              selectedTab={INVOICE_TAB_INDEX.OFFICE}
              needSave={notYetSaved}
              error={hasError}
              onSaveAndCommit={() => onSaveAndCommit()}
              onChangeStatus={(status) => onChangeInvoiceStatus(status)}
              onMasterChange={() => loadAllData()}
            />
          </div>
          <div  className={styles.tabs}>
            <div className={styles.tabs__body}>
              {invoiceMasterData &&
                <OfficeWorkFee
                  targetInvoice={invoiceMasterData}
                  tabValues={officeWorkFees}
                  officeWorkCategoryOptions={officeWorkOptions}
                  isChanged={isChanged}
                  onNotYetConfirmed={() => onChangeInvoiceStatus(InvStatus.NOT_CONFIRMED)}
                  onReset={() => loadAllData()}
                  onSave={() => saveOfficeWorkFee()}
                  onChange={(items) => {onChangeFees(items); setIsChanged(true)}}
                />
              }
            </div>
          </div>
        </div>
      </React.Fragment>
    </Layout>
  )
}

export default InvoiceOfficeWorkFeePage